import type { QueryClient } from '@tanstack/react-query';
import type { Occurrence } from '@klopotun/contracts';
import {
  attentionQueryRoot,
  bankQueryRoot,
  paymentQueryRoot,
  summaryQueryRoot,
} from '../../../../shared/api/index.js';
import { occurrenceQueries } from '../queries/occurrence-queries.js';

/**
 * Every write moves a period between listings, so the whole list branch goes, not one filter.
 * The payment branch goes with it: the answer "apply always" rewrites the template amount along
 * with the period (design note D3), and the payment screens read the template, not the period.
 *
 * The occurrence branch goes whole rather than by the one period that was written: "apply always"
 * rewrites the expected amount of every open period of that payment, and which rows that touched
 * is a decision taken on the server, not one this side can guess - the same reason the payment
 * side gives in invalidatePayment.
 */
export const invalidateOccurrence = async (
  queryClient: QueryClient,
  occurrence: Occurrence,
): Promise<void> => {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: occurrenceQueries.all() }),
    queryClient.invalidateQueries({ queryKey: paymentQueryRoot.lists() }),
    queryClient.invalidateQueries({
      queryKey: [...paymentQueryRoot.details(), occurrence.paymentId],
    }),
    queryClient.invalidateQueries({ queryKey: summaryQueryRoot.all() }),
    queryClient.invalidateQueries({ queryKey: bankQueryRoot.all() }),
    queryClient.invalidateQueries({ queryKey: attentionQueryRoot.all(), refetchType: 'all' }),
  ]);
};
